'use client';

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, CheckCheck, BellOff } from 'lucide-react';
import { useNotificationCenter } from '@/providers/NotificationCenterProvider';
import { NotificationCard } from './NotificationCard';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';

export function NotificationDrawer() {
    const {
        isDrawerOpen,
        closeDrawer,
        notifications,
        unreadCount,
        isLoading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loadMore,
        hasMore,
    } = useNotificationCenter();

    const router = useRouter();
    const { buildPath } = useBasePath();
    const scrollContainerRef = useRef<HTMLDivElement>(null);



    // Handle scroll for infinite loading
    const handleScroll = () => {
        if (!scrollContainerRef.current || isLoading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        if (scrollPercentage > 0.8) {
            loadMore();
        }
    };

    // Close drawer on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isDrawerOpen) {
                closeDrawer();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isDrawerOpen, closeDrawer]);

    // Prevent body scroll when drawer is open
    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isDrawerOpen]);

    const handleSettingsClick = () => {
        closeDrawer();
        router.push(buildPath('/account/notifications'));
    };

    return (
        <AnimatePresence>
            {isDrawerOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={closeDrawer}
                    />

                    {/* Drawer */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col max-w-[480px]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
                                {unreadCount > 0 && (
                                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                        {unreadCount}
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {/* Mark all as read */}
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                        title="Mark all as read"
                                    >
                                        <CheckCheck className="w-5 h-5 text-gray-600" />
                                    </button>
                                )}

                                {/* Settings */}
                                <button
                                    onClick={handleSettingsClick}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Notification settings"
                                >
                                    <Settings className="w-5 h-5 text-gray-600" />
                                </button>

                                {/* Close */}
                                <button
                                    onClick={closeDrawer}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    aria-label="Close"
                                >
                                    <X className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>
                        </div>

                        {/* Notification List */}
                        <div
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            className="flex-1 overflow-y-auto p-4 space-y-3"
                        >
                            {notifications.length === 0 && !isLoading ? (
                                // Empty state
                                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <BellOff className="w-10 h-10 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        No notifications yet
                                    </h3>
                                    <p className="text-sm text-gray-500 max-w-xs">
                                        When you receive notifications, they'll appear here
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {notifications.map((notification) => (
                                        <NotificationCard
                                            key={notification._id}
                                            notification={notification}
                                            onMarkAsRead={markAsRead}
                                            onDelete={deleteNotification}
                                        />
                                    ))}

                                    {/* Loading indicator */}
                                    {isLoading && (
                                        <div className="flex justify-center py-4">
                                            <div className="w-6 h-6 border-2 border-hprimary-600 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    )}

                                    {/* End of list indicator */}
                                    {!hasMore && notifications.length > 0 && (
                                        <div className="text-center py-4 text-sm text-gray-500">
                                            You're all caught up! 🎉
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
